// Record encryption envelope (Implementation Plan v02 §6.2, Add9 F4; I-1a Batch 3).
//
// AES-GCM 256, a new random 96-bit nonce on every write, 128-bit tag. What stays
// in clear is only the header: record id (random UUID v4), clear kind, schema
// version, key id, nonce. The header is bound as AES-GCM additional data, so a
// record cannot be swapped to another id, relabelled, or replayed under another
// schema version — any change makes `openRecord` fail.
//
// Batch 3 decisions (Aldo, 2026-10-14):
//   1. Generic kinds — every diary record carries the one clear kind `record`;
//      its real kind sits inside the ciphertext. Photo blobs are `blob`.
//   2. Size buckets — plaintext is padded (padding.ts) before encryption.
//   3. Per-moment keys deferred to v1.1 — `keyId` is always the data key for
//      records in v1.0; a photo blob's key lives inside its moment's record.

import { type Bytes, randomBytes, utf8 } from './bytes'
import { isRecordId } from './ids'
import { pad, recordSize, type SizeRule, unpad } from './padding'

export type ClearKind = 'record' | 'blob'

/** Key id for records sealed with the data key (the only record key in v1.0). */
export const DATA_KEY_ID = 'dk'
/** Key id for photo blobs: the key is held inside the owning moment's record. */
export const PHOTO_KEY_ID = 'photo'

const NONCE_BYTES = 12
const TAG_BITS = 128
const AAD_LABEL = 'feel-record-1'

export interface RecordHeader {
  readonly id: string
  readonly kind: ClearKind
  readonly schemaVersion: number
  readonly keyId: string
}

/** What is written to IndexedDB: the clear header + nonce + ciphertext. */
export interface SealedRecord extends RecordHeader {
  readonly iv: Bytes
  readonly ct: Bytes
}

export class RecordOpenError extends Error {
  constructor(message = 'record could not be opened') {
    super(message)
    this.name = 'RecordOpenError'
  }
}

function checkHeader(header: RecordHeader): void {
  if (!isRecordId(header.id)) {
    throw new Error('record id must be a UUID v4')
  }
  if (header.kind !== 'record' && header.kind !== 'blob') {
    throw new Error('unknown clear kind')
  }
  if (!Number.isInteger(header.schemaVersion) || header.schemaVersion < 1) {
    throw new Error('schema version must be a positive integer')
  }
  if (header.keyId.length === 0) {
    throw new Error('key id must not be empty')
  }
}

/** Unambiguous encoding of the header for AES-GCM additional data. */
export function headerAad(header: RecordHeader): Bytes {
  return utf8(JSON.stringify([AAD_LABEL, header.id, header.kind, header.schemaVersion, header.keyId]))
}

/** The raw AES-GCM primitive with the fixed tag length (exported for known-answer tests). */
export async function aesGcmEncrypt(key: CryptoKey, iv: Bytes, aad: Bytes, plaintext: Bytes): Promise<Bytes> {
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: aad, tagLength: TAG_BITS }, key, plaintext)
  return new Uint8Array(ct)
}

export async function aesGcmDecrypt(key: CryptoKey, iv: Bytes, aad: Bytes, ciphertext: Bytes): Promise<Bytes> {
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv, additionalData: aad, tagLength: TAG_BITS }, key, ciphertext)
  return new Uint8Array(pt)
}

export async function sealRecord(
  key: CryptoKey,
  header: RecordHeader,
  plaintext: Uint8Array,
  sizeRule: SizeRule = recordSize,
): Promise<SealedRecord> {
  checkHeader(header)
  const iv = randomBytes(NONCE_BYTES)
  const padded = pad(plaintext, sizeRule)
  try {
    const ct = await aesGcmEncrypt(key, iv, headerAad(header), padded)
    return { id: header.id, kind: header.kind, schemaVersion: header.schemaVersion, keyId: header.keyId, iv, ct }
  } finally {
    padded.fill(0)
  }
}

/** Decrypt + check the header binding + strip padding. Any failure is a RecordOpenError. */
export async function openRecord(key: CryptoKey, sealed: SealedRecord): Promise<Bytes> {
  try {
    checkHeader(sealed)
    if (sealed.iv.length !== NONCE_BYTES) {
      throw new Error('bad nonce length')
    }
    const padded = await aesGcmDecrypt(key, sealed.iv, headerAad(sealed), sealed.ct)
    try {
      return unpad(padded)
    } finally {
      padded.fill(0)
    }
  } catch (error) {
    if (error instanceof RecordOpenError) {
      throw error
    }
    throw new RecordOpenError()
  }
}
